import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { DeleteTeeTimeAccessCode, GetTeeTimeAccessCodes, ITeeTimeAccessCode, PostTeeTimeAccessCode, PutTeeTimeAccessCode } from "api/rpc/2024-04/facilityAdmin/teesheet/accessCode";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Toggle from "components/form/toggle/Toggle";
import Page from "components/page/Page";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import TimePick from "components/timePick/TimePick";
import Portal from "elements/Portal";
import { formatDate } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError, showSuccess } from "redux/actions/ui";

const TABLE_LIMIT = 25;

export default function AccessCodes() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const {authStore} = useAppSelector(store => store);
    const [accessCodes, setAccessCodes] = useState<Array<ITeeTimeAccessCode>>(undefined);
    const [offset, setOffset] = useState<number>(0);
    const permissions = authStore?.user?.permissions;
    const {state: accessCodeModal, updateModal: updateAccessCodeModal, closeModal: closeAccessCodeModal} 
    = useModal<{accessCodeRecord: ITeeTimeAccessCode; accessCode: string; startDate: Date; endDate: Date; daysInAdvance: string; active: boolean; openTime: string}>({accessCodeRecord: null, accessCode: "", startDate: new Date(), endDate: new Date(), daysInAdvance: "", active: true, openTime: ""});
    const {state: deleteCodeModal, updateModal: updateDeleteCodeModal, closeModal: closeDeleteCodeModal} = useModal<{accessCode: ITeeTimeAccessCode}>({accessCode: null});

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getTeeTimeAccessCodes(source.token);
        return () => source.cancel();
    }, [offset]);

    async function getTeeTimeAccessCodes(cancelToken?: CancelToken) {
        if (accessCodes !== undefined) {
            //loader
            setAccessCodes(undefined);
        }
        const res = await GetTeeTimeAccessCodes({limit: TABLE_LIMIT, offset: offset}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setAccessCodes([]);
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting access codes")));
            return;
        }
        setAccessCodes(res?.data);
    }

    async function createAccessCode() {
        if (accessCodeModal?.startDate > accessCodeModal?.endDate) {
            dispatch(showError(t("Start date must be before end date")));
            return;
        }

        if (accessCodeModal?.daysInAdvance === "" || (!isNaN(Number(accessCodeModal?.daysInAdvance)) && Number(accessCodeModal?.daysInAdvance) < 0)) {
            dispatch(showError(t("Valid days in advance value required")));
            return;
        }

        const res = await PostTeeTimeAccessCode({
            access_code: accessCodeModal?.accessCode ?? null, 
            start_date: formatDate(accessCodeModal?.startDate),
            end_date: formatDate(accessCodeModal?.endDate),
            days_in_advance: Number(accessCodeModal?.daysInAdvance),
            active: accessCodeModal?.active,
            open_time: accessCodeModal?.openTime,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error creating access code")));
            return;
        }

        dispatch(showSuccess(t("Successfully created access code")));
        void closeAccessCodeModal();
        //Reload access codes
        void getTeeTimeAccessCodes();
    }

    async function updateAccessCode() {
        if (!accessCodeModal?.accessCodeRecord) {
            dispatch(showError(t("Error selecting access code")));
            return;
        }

        if (accessCodeModal?.startDate > accessCodeModal?.endDate) {
            dispatch(showError(t("Start date must be before end date")));
            return;
        }

        if (accessCodeModal?.daysInAdvance === "" || (!isNaN(Number(accessCodeModal?.daysInAdvance)) && Number(accessCodeModal?.daysInAdvance) < 0)) {
            dispatch(showError(t("Valid days in advance value required")));
            return;
        }

        const res = await PutTeeTimeAccessCode({
            id: accessCodeModal?.accessCodeRecord?.id,
            access_code: accessCodeModal?.accessCode ?? null, 
            start_date: formatDate(accessCodeModal?.startDate),
            end_date: formatDate(accessCodeModal?.endDate),
            days_in_advance: Number(accessCodeModal?.daysInAdvance),
            active: accessCodeModal?.active,
            open_time: accessCodeModal?.openTime,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error updating access code")));
            return;
        }

        dispatch(showSuccess(t("Successfully updated access code")));
        void closeAccessCodeModal();
        //Reload access codes
        void getTeeTimeAccessCodes();
    }

    async function handleDeleteAccessCode() {
        if (!deleteCodeModal?.accessCode) {
            return;
        }
        const res = await DeleteTeeTimeAccessCode({id: deleteCodeModal?.accessCode?.id}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting access code")));
            return;
        }

        dispatch(showSuccess(t("Successfully deleted access code")));
        void closeDeleteCodeModal();
        //Reload access codes
        void getTeeTimeAccessCodes();
    }

    return (
        <Page
        title={t("Access Codes")}
        narrow
        primaryAction={{content: t("New"), action: () => updateAccessCodeModal({isOpen: true, accessCodeRecord: null}), disabled: !permissions?.tee_sheet_access_code}}
        subtitle={"Create access codes that open tee times in advance"}
      >
        <DataTable
        columns={[
          { label: t("Access Code") },
          { label: t("Start Date") },
          { label: t("End Date") },
          { label: t("Days in Advance") },
          { label: t("Status") },
          { label: ""},
        ]}
        loading={accessCodes === undefined}
        footer={{
            tableLimit: TABLE_LIMIT,
            tableOffset: offset,
            disableNextOffset: !accessCodes || !(accessCodes?.length === TABLE_LIMIT),
            handleTableOffset: direction =>
              setOffset(prevOffset => direction === "prev" ? prevOffset - TABLE_LIMIT : prevOffset + TABLE_LIMIT),
          }}
      >
        {accessCodes?.map((accessCode, index) => {
            return (
                <tr key={index} className={permissions?.tee_sheet_access_code ? "clickable" : ""} onClick={!permissions?.tee_sheet_access_code ? undefined : () => 
                updateAccessCodeModal({
                    isOpen: true, 
                    startDate: new Date(moment(accessCode?.start_date, "YYYY-MM-DD")?.format("YYYY-MM-DD")), 
                    endDate: new Date(moment(accessCode?.end_date, "YYYY-MM-DD")?.format("YYYY-MM-DD")), 
                    daysInAdvance: accessCode?.days_in_advance?.toString(), 
                    openTime: accessCode?.open_time ?? "",
                    accessCode: accessCode?.access_code,
                    active: accessCode?.active,
                    accessCodeRecord: accessCode,
                    })}>
                    <td>{accessCode?.access_code}</td>
                    <td>{moment(accessCode?.start_date, "YYYY-MM-DD")?.format("ll")}</td>
                    <td>{moment(accessCode?.end_date, "YYYY-MM-DD")?.format("ll")}</td>
                    <td>{accessCode?.days_in_advance}</td>
                    <td><Badge size="medium" type={accessCode?.active ? "success" : "error"}>{accessCode?.active ? t("Active") : t("Inactive")}</Badge></td>
                    {permissions?.tee_sheet_access_code ? (
                      <td onClick={e => {
                        e.stopPropagation(); 
                        void updateDeleteCodeModal({isOpen: true, accessCode: accessCode});
                    }}><FontAwesomeIcon icon={faTrash} /></td>
                    ) : <td></td>} 
                </tr>
            );
        })}
      </DataTable>


        <Portal>
            <Sheet
            open={accessCodeModal?.isOpen}
            title={accessCodeModal?.accessCodeRecord ? t("Edit Access Code") : t("New Access Code")}
            size="medium"
            onCancel={closeAccessCodeModal}
            onOk={accessCodeModal?.accessCodeRecord ? () => updateAccessCode() : () => createAccessCode()}
            okText={accessCodeModal?.accessCodeRecord ? t("Save Changes") : t("Create")}
            overflow
            closable
            height="flexible"
            >
            <FormLayout>
                <FormLayout.Group>
                <DatePickerInput
                    months={1}
                    position="left"
                    startingDate={accessCodeModal?.startDate}
                    setStartingDate={date => updateAccessCodeModal({startDate: date as Date})}
                    label={t("Start Date")}
                />
                <DatePickerInput
                    months={1}
                    position="left"
                    startingDate={accessCodeModal?.endDate}
                    setStartingDate={date => updateAccessCodeModal({endDate: date as Date})}
                    label={t("End Date")}
                />
                </FormLayout.Group>
                <FormLayout.Group>
                    <Input 
                        label={t("Days In Advance")} 
                        onChange={e => updateAccessCodeModal({daysInAdvance: e.target.value})} 
                        value={accessCodeModal?.daysInAdvance} 
                        placeholder={"30"}
                        tooltipText={t("Number of days in advance that tee times are accessible using this access code")}
                        type="number"
                    />

                    <TimePick
                        value={accessCodeModal?.openTime}
                        onChange={timeString => updateAccessCodeModal({openTime: timeString})}
                        size="large"
                        align="right"
                        label={t("Open Time")}
                        toolTipText={t("Specify the time when tee times open on the last day of the access window")}
                        />
                </FormLayout.Group>
                <FormLayout.Group>
                    <Input 
                        label={t("Access Code")} 
                        onChange={e => updateAccessCodeModal({accessCode: e.target.value})} 
                        value={accessCodeModal?.accessCode} 
                        placeholder={t("Access Code")}
                        tooltipText={t("Leave empty to generate a random code")} 
                        characterCountBottom
                        maxLength={20}
                    />
                    <div style={{marginBottom: "2rem"}}>
                        <Toggle
                            labelRight={t("Active")}
                            onChange={e => updateAccessCodeModal({active: e.target.checked})}
                            checked={accessCodeModal?.active}
                        />
                    </div>
                </FormLayout.Group>
            </FormLayout>
            </Sheet>
        </Portal>
        <Portal isMounted={deleteCodeModal?.isOpen}>
            <Popup 
                open={deleteCodeModal?.isOpen} 
                type="warning" 
                title={t("Delete Access Code")} 
                description={`${t("Are you sure you want to delete access code: ")} ${deleteCodeModal?.accessCode?.access_code}? ${t("This cannot be undone")}`} 
                onCancel={closeDeleteCodeModal} 
                okDisabled={!deleteCodeModal?.accessCode?.id}
                onOk={handleDeleteAccessCode}
                okText={t("Delete")}
            />
        </Portal>
        </Page>
    );
}